import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import { showToastAction } from '../ui';
import {
    fetchSettings,
    settingsFetched,
    settingsFetchFailed,
    saveField,
    saveFieldSucceeded,
    saveFieldFailed,
    saveReferencesView,
    saveReferencesViewSucceeded,
    saveReferencesViewFailed,
    sendMessage,
    sendMessageSucceeded,
    sendMessageFailed,
    testPendingPayment,
    testPendingPaymentSettled,
    mapSettingsDocToUi,
} from '../platformSettings';

// UI field key → canonical DB field name. Two renames, plus the support-chat
// switches, which live under one nested object in the document; everything
// else is identity.
const FIELD_TO_API_KEY = {
    enablePayBadge: 'hour24PayAvailableBadge',
    enableReferralBannerPopup: 'enableReferralBanner',
    supportChatStaffersApproved: 'supportChat.staffersApproved',
    supportChatStaffersOnboarding: 'supportChat.staffersOnboarding',
    supportChatClientsApproved: 'supportChat.clientsApproved',
    supportChatClientsOnboarding: 'supportChat.clientsOnboarding',
};
const toApiKey = key => FIELD_TO_API_KEY[key] || key;

// Human-readable success toast per saved field.
const SAVE_SUCCESS_TEXT = {
    enableDisableOnboarding: 'Onboarding regions updated',
};

function* fetchSettingsEffect() {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.platformSettings.get,
            method: 'GET',
            queryParams: {},
        });
        const doc =
            (response && Array.isArray(response.results)
                ? response.results[0]
                : null) || {};
        yield put(settingsFetched(mapSettingsDocToUi(doc)));
    } catch (e) {
        yield put(settingsFetchFailed(e.message));
        yield put(
            showToastAction({
                type: 'error',
                text: e.message || 'Failed to load settings',
            }),
        );
    }
}

function* saveFieldEffect({ payload }) {
    const { key, value } = payload;
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.platformSettings.update,
            method: 'PATCH',
            body: { [toApiKey(key)]: value },
        });
        yield put(saveFieldSucceeded({ key }));
        yield put(
            showToastAction({
                type: 'success',
                text: SAVE_SUCCESS_TEXT[key] || 'Setting updated',
            }),
        );
    } catch (e) {
        yield put(saveFieldFailed({ key }));
        yield put(
            showToastAction({
                type: 'error',
                text: e.message || 'Failed to update setting',
            }),
        );
    }
}

function* saveReferencesViewEffect({ payload }) {
    const { cityKey, city, value } = payload;
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.platformSettings.referencesView,
            method: 'PUT',
            body: { city, enabled: value },
        });
        yield put(saveReferencesViewSucceeded({ cityKey }));
        yield put(
            showToastAction({
                type: 'success',
                text: 'References view updated',
            }),
        );
    } catch (e) {
        yield put(saveReferencesViewFailed({ cityKey }));
        yield put(
            showToastAction({
                type: 'error',
                text: e.message || 'Failed to update references view',
            }),
        );
    }
}

function* sendMessageEffect({ payload }) {
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.platformSettings.messageOfTheDay,
            method: 'PUT',
            body: {
                messageOfDay: payload.messageOfDay,
                messageOfDayInFrench: payload.messageOfDayInFrench,
            },
        });
        yield put(
            sendMessageSucceeded({
                messageOfDay: payload.messageOfDay,
                messageOfDayInFrench: payload.messageOfDayInFrench,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Message of the day sent',
            }),
        );
    } catch (e) {
        yield put(sendMessageFailed());
        yield put(
            showToastAction({
                type: 'error',
                text: e.message || 'Failed to send message',
            }),
        );
    }
}

function* testPendingPaymentEffect({ payload }) {
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.platformSettings.pendingPaymentTest(
                payload.province,
            ),
            method: 'GET',
            queryParams: {},
        });
        yield put(
            showToastAction({
                type: 'success',
                text: `Pending-payment test triggered for ${payload.province}.`,
            }),
        );
    } catch (e) {
        yield put(
            showToastAction({
                type: 'error',
                text: e.message || 'Failed to run pending-payment test',
            }),
        );
    } finally {
        yield put(testPendingPaymentSettled());
    }
}

export const platformSettingsSagas = [
    takeLatest(fetchSettings.type, fetchSettingsEffect),
    takeEvery(saveField.type, saveFieldEffect),
    takeEvery(saveReferencesView.type, saveReferencesViewEffect),
    takeLatest(sendMessage.type, sendMessageEffect),
    takeLatest(testPendingPayment.type, testPendingPaymentEffect),
];
